/**
 * Validation Utilities for Signup Forms
 * Provides reusable validation functions for National ID, VAT number, and file uploads
 */

// Constants
export const MAX_FILE_SIZE_MB = 5;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // 5MB in bytes

// National ID / Passport / National ID Format: Alphanumeric and dashes
export const EMIRATES_ID_REGEX = /^[a-zA-Z0-9-]+$/;

// VAT Number Format: Digits only
export const VAT_NUMBER_REGEX = /^[0-9]+$/;

/**
 * Format National ID with dashes: 784-YYYY-NNNNNNN-N
 * Only formats if it looks like an National ID (starts with 784 and contains only digits/dashes)
 * Otherwise, it returns the value as-is for passport numbers.
 * @param {string} value - Input value
 * @returns {string} Formatted National ID or original value
 */
export const formatEmiratesId = (value) => {
    if (!value) return '';

    // If it doesn't look like an National ID (doesn't start with 784), don't auto-format
    if (!value.startsWith('784') && value.length > 0) {
        // Allow alphanumeric and dashes for passports/national IDs
        return value.replace(/[^a-zA-Z0-9-]/g, '');
    }

    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Apply formatting based on length
    let formatted = digits;

    if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    if (digits.length > 7) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    }
    if (digits.length > 14) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 14)}-${digits.slice(14, 15)}`;
    }

    return formatted;
};

/**
 * Validate National ID format
 * @param {string} value - National ID to validate
 * @returns {boolean} True if valid format
 */
export const validateEmiratesIdFormat = (value) => {
    return EMIRATES_ID_REGEX.test(value);
};

/**
 * Format VAT Number (digits only)
 * @param {string} value - Input value
 * @returns {string} Formatted VAT number (digits only)
 */
export const formatVatNumber = (value) => {
    if (!value) return '';

    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    return digits;
};

/**
 * Validate VAT Number format
 * @param {string} value - VAT number to validate
 * @returns {boolean} True if valid format (15 digits)
 */
export const validateVatNumberFormat = (value) => {
    return VAT_NUMBER_REGEX.test(value);
};

/**
 * Validate file size
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB (default: 5)
 * @returns {boolean} True if file size is within limit
 */
export const validateFileSize = (file, maxSizeMB = MAX_FILE_SIZE_MB) => {
    if (!file) return false;

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
};

/**
 * Validate file type
 * @param {File} file - File to validate
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @returns {boolean} True if file type is allowed
 */
export const validateFileType = (file, allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']) => {
    if (!file) return false;

    return allowedTypes.includes(file.type);
};

/**
 * Get file size in MB
 * @param {File} file - File to get size for
 * @returns {number} File size in MB
 */
export const getFileSizeMB = (file) => {
    if (!file) return 0;

    return (file.size / (1024 * 1024)).toFixed(2);
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};



export const validatePassword = (password) => {
    if (!password) {
        return "auth.signup.validation.passwordRequired";
    }

    if (password.length < 8) {
        return "auth.signup.validation.passwordMin";
    }

    if (!/[A-Z]/.test(password)) {
        return "auth.signup.validation.passwordUppercase";
    }

    if (!/[a-z]/.test(password)) {
        return "auth.signup.validation.passwordLowercase";
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
        return "auth.signup.validation.passwordSpecial";
    }

    return null;
};